/** Small numeric helpers shared across the DSP modules. */

export const EPS = 1e-20;

export function toDb(magnitude: number, reference = 1): number {
  return 20 * Math.log10(Math.max(Math.abs(magnitude), EPS) / Math.max(Math.abs(reference), EPS));
}

export function fromDb(db: number): number {
  return Math.pow(10, db / 20);
}

export function powerToDb(power: number, reference = 1): number {
  return 10 * Math.log10(Math.max(power, EPS) / Math.max(reference, EPS));
}

export function toFloat64(x: ArrayLike<number>): Float64Array {
  if (x instanceof Float64Array) return x;
  const out = new Float64Array(x.length);
  for (let i = 0; i < x.length; i++) out[i] = x[i];
  return out;
}

export interface PeakLocation {
  /** Integer index of the largest |sample|. */
  index: number;
  /** Sub-sample index from a parabolic fit of the three samples around the peak. */
  fractionalIndex: number;
  /** Signed value at the integer peak. */
  value: number;
  /** |value| at the peak. */
  magnitude: number;
}

/**
 * Largest absolute sample, refined to sub-sample resolution.
 *
 * At 48 kHz one sample is 3.6 mm of range, and the parabolic fit is worth
 * roughly another order of magnitude on a clean peak - which matters because
 * every echo distance is measured relative to this point.
 */
export function findPeak(x: ArrayLike<number>, from = 0, to = x.length): PeakLocation {
  let index = from;
  let magnitude = -1;
  for (let i = from; i < to; i++) {
    const m = Math.abs(x[i]);
    if (m > magnitude) {
      magnitude = m;
      index = i;
    }
  }
  return { index, fractionalIndex: refinePeak(x, index), value: x[index] ?? 0, magnitude };
}

/** Parabolic interpolation through |x| at index-1, index, index+1. */
export function refinePeak(x: ArrayLike<number>, index: number): number {
  if (index <= 0 || index >= x.length - 1) return index;
  const a = Math.abs(x[index - 1]);
  const b = Math.abs(x[index]);
  const c = Math.abs(x[index + 1]);
  const denom = a - 2 * b + c;
  if (Math.abs(denom) < EPS) return index;
  const delta = (0.5 * (a - c)) / denom;
  return Number.isFinite(delta) && Math.abs(delta) <= 1 ? index + delta : index;
}

export function rms(x: ArrayLike<number>, from = 0, to = x.length): number {
  let sum = 0;
  const n = Math.max(0, to - from);
  for (let i = from; i < to; i++) sum += x[i] * x[i];
  return n > 0 ? Math.sqrt(sum / n) : 0;
}

export function maxAbs(x: ArrayLike<number>): number {
  let m = 0;
  for (let i = 0; i < x.length; i++) {
    const v = Math.abs(x[i]);
    if (v > m) m = v;
  }
  return m;
}

/** Scale a copy so its largest absolute value equals `target`. */
export function normalize(x: ArrayLike<number>, target = 1): Float64Array {
  const out = toFloat64(x).slice();
  const peak = maxAbs(out);
  if (peak <= EPS) return out;
  const g = target / peak;
  for (let i = 0; i < out.length; i++) out[i] *= g;
  return out;
}

/** Least-squares fit of y = slope*x + intercept, with the coefficient of determination. */
export function linearRegression(
  xs: ArrayLike<number>,
  ys: ArrayLike<number>,
): { slope: number; intercept: number; r2: number } {
  const n = Math.min(xs.length, ys.length);
  if (n < 2) return { slope: 0, intercept: 0, r2: 0 };
  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) { sx += xs[i]; sy += ys[i]; }
  const mx = sx / n, my = sy / n;
  let sxy = 0, sxx = 0, syy = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx;
    const dy = ys[i] - my;
    sxy += dx * dy;
    sxx += dx * dx;
    syy += dy * dy;
  }
  if (sxx < EPS) return { slope: 0, intercept: my, r2: 0 };
  const slope = sxy / sxx;
  const r2 = syy < EPS ? 1 : (sxy * sxy) / (sxx * syy);
  return { slope, intercept: my - slope * mx, r2 };
}

/** Half-Hann fade in and out, applied in place. Kills the click at both ends. */
export function applyFades(x: Float64Array, fadeInSamples: number, fadeOutSamples: number): void {
  const n = x.length;
  const fin = Math.min(Math.max(0, Math.floor(fadeInSamples)), n);
  const fout = Math.min(Math.max(0, Math.floor(fadeOutSamples)), n);
  for (let i = 0; i < fin; i++) x[i] *= 0.5 - 0.5 * Math.cos((Math.PI * i) / fin);
  for (let i = 0; i < fout; i++) {
    const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / fout);
    x[n - 1 - i] *= w;
  }
}

/** Moving-average smoother over `windowSamples`, centred, edge-clamped. */
export function movingAverage(x: ArrayLike<number>, windowSamples: number): Float64Array {
  const n = x.length;
  const out = new Float64Array(n);
  const half = Math.max(0, Math.floor(windowSamples / 2));
  if (half === 0) return toFloat64(x).slice();
  const cumulative = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) cumulative[i + 1] = cumulative[i] + x[i];
  for (let i = 0; i < n; i++) {
    const lo = Math.max(0, i - half);
    const hi = Math.min(n, i + half + 1);
    out[i] = (cumulative[hi] - cumulative[lo]) / (hi - lo);
  }
  return out;
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
